import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTheme } from "@mui/material/styles";
import { paramCase } from "change-case";
import { useNavigate } from "react-router";
import { useLocales } from "../../../../locales";
import Chip from "../../../../components/Chip";
import useResponsive from "../../../../hooks/useResponsive";
import Iconify from "../../../../components/iconify";
import OpeningHours from "../../../../components/OpeningHours";
import {
  formatMinutesToHours,
  mapReservationStatusToColor,
  mapReservationStatusToTranslation,
} from "../../../../utils/reservationFunctions";
import { fDate, fTime, getDateWithoutTimezoneChange } from "../../../../utils/formatTime";
import { RESERVATION_CONSTANTS } from "../../../../config/constants/reservation-constants";
import GuestsIcon from "src/assets/icons/booking/GuestsIcon";
import LengthIcon from "src/assets/icons/booking/LengthIcon";
import HoldingFeeIcon from "src/assets/icons/booking/HoldingFeeIcon";
import { LocationIcon } from "src/assets/icons/LocationIcon";
import { ClockIcon } from "src/assets/icons/ClockIcon";
import DateIcon from "src/assets/icons/DateIcon";
import { PATHS } from "src/routes/paths";
import LinkedEventCard from "src/components/LinkedEventCard";
import { PrimaryButton } from "./listingButtons";
import TicketIcon from "src/assets/icons/booking/TicketIcon";
import MenuItemIcon from "src/assets/icons/booking/MenuItemIcon";
import { currencyMap } from "src/utils/currencies";

const STATUS = RESERVATION_CONSTANTS.RESERVATION_STATUS;
const VENUE_TIMEZONE = "Europe/Bucharest";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const fmtNumber = (n) => (Number.isInteger(n) ? n : Number(n).toFixed(2));
const formatMoney = (value, currency) =>
  `${Number.isInteger(value) ? value : Number(value).toFixed(2)} ${currency ?? ""}`.trim();

const getDollarSigns = (number) => (number ? "$".repeat(number) : "");

// same logic as EventDetailsSummary
const formatEventDate = (start, end) => {
  const opts = { year: "numeric", month: "short", day: "numeric" };
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (start?.split("T")[0] === end?.split("T")[0]) {
    return startDate.toLocaleDateString(undefined, opts);
  }
  return `${startDate.toLocaleDateString(undefined, opts)} - ${endDate.toLocaleDateString(undefined, opts)}`;
};

function getVenueWallClockNowMs() {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: VENUE_TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(new Date())
      .map((p) => [p.type, p.value]),
  );
  return Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
}

function SectionCard({ children, sx }) {
  return (
    <Card sx={{ p: 2, ...sx }}>
      {children}
    </Card>
  );
}

function DetailItem({ icon, children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      {icon}
      <Typography variant="body2" noWrap>
        {children}
      </Typography>
    </Stack>
  );
}

function BookingDetailsCard({
  eventData,
  bookingData,
  cancelBooking,
  reconfirmBooking,
  payForBooking,
  paying = false,
}) {
  const [openAlert, setOpenAlert] = useState(false);
  const [email, setEmail] = useState("");
  const { translate } = useLocales();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useResponsive("down", "sm");

  const params = new URLSearchParams(window.location.search);
  const isEmbedded = params.get("embed") === "true" || window.self !== window.top;

  // ---------- stage ----------
  const paymentRequest =
    bookingData.status === STATUS.PAYMENT_REQUESTED ? bookingData.paymentRequest : null;
  const orders = bookingData.orders ?? [];

  const emailValid = EMAIL_REGEX.test(email.trim());
  const canPay = !paying && (!paymentRequest?.emailRequired || emailValid);

  // ---------- reconfirmation (unchanged logic) ----------
  const canReconfirm = (() => {
    const { status, clientStatus, bookedHour } = bookingData;
    const { reminderThresholdHours, noActionThresholdHours } = eventData?.booking ?? {};
    if (
      status !== STATUS.CONFIRMED ||
      clientStatus !== "UNCONFIRMED_ARRIVAL" ||
      reminderThresholdHours == null ||
      noActionThresholdHours == null
    ) {
      return false;
    }
    // bookedHour is the venue wall-clock time serialized without a zone
    const bookedMs = Date.parse(`${String(bookedHour).replace(/Z$/, "")}Z`);
    const nowMs = getVenueWallClockNowMs();
    const H = 60 * 60 * 1000;
    return nowMs >= bookedMs - reminderThresholdHours * H && nowMs <= bookedMs - noActionThresholdHours * H;
  })();

  // ---------- event / venue summary data ----------
  const general = eventData?.general ?? {};
  const address = eventData?.address ?? {};
  const { startDate, endDate, isPermanent } = general;
  const openingHours = general.openingHours ?? [];
  const { rating, reviewsCount, priceRangeStart, priceRangeEnd, program } = eventData?.rating ?? {};

  // Permanent (venue) = has opening hours / program; otherwise a dated event

  const hasAddress = !!(address.city && address.street);
  const addressText =
    (address.venueName ? `${address.venueName}, ` : "") + `${address.street}, ${address.city}`;
  const googleMapsLink = address.googleLocationLink;

  const heroImage = general.images?.[0];

  const goToEvent = () => {
    if (!general.id) return;
    navigate(
      PATHS.listings.view(
        paramCase(general.subCategory?.name),
        paramCase(general.name),
        paramCase(general.id),
      ) + (isEmbedded ? "?embed=true" : ""),
    );
  };

  // ---------- derived data ----------
  const organizer = bookingData.organizer;
  const guestName = `${organizer?.firstName ?? ""} ${organizer?.lastName ?? ""}`.trim();
  const guestPhone = organizer?.account?.phone;
  const guestEmail = organizer?.account?.email;

  const areaNames = [
    ...new Set(
      (bookingData.tables ?? []).map((table) => table?.area?.name).filter(Boolean),
    ),
  ];
  if (!areaNames.length && bookingData.requestedArea?.name) areaNames.push(bookingData.requestedArea.name);

  const goToLinkedEvent = (e) => {
    navigate(PATHS.listings.view(paramCase(e.subCategory?.name), paramCase(e.name), paramCase(e.id)));
  };

  const mapStatusToEmailSubtitle = () => {
    switch (bookingData.status) {
      case STATUS.CONFIRMED:
        return eventData.marketing?.emailSubtitleApproved;
      case STATUS.WAITLIST:
        return eventData.marketing?.emailSubtitleWaitlist;
      case STATUS.PENDING:
        return eventData.marketing?.emailSubtitlePending;
      case STATUS.DELETED_BY_VENUE:
      case STATUS.CANCEL_BY_VENUE:
      case STATUS.DENIED:
        return eventData.marketing?.emailSubtitleCancelled;
      default:
        return "";
    }
  };

  const subtitle = mapStatusToEmailSubtitle();
  const canCancel = ![STATUS.CANCEL_BY_USER, STATUS.DENIED].includes(bookingData.status);

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          alignItems: "start",
          width: 1,
          maxWidth: { xs: 720, md: 1100 },
          mx: "auto",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 5fr) minmax(0, 7fr)" },
        }}
      >
        {/* Left column: image + event/venue summary */}
        <Card sx={{ overflow: "visible" }}>
          <Box
            onClick={heroImage ? goToEvent : undefined}
            sx={{
              cursor: heroImage ? "pointer" : "default",
              backgroundImage: heroImage ? `url(${heroImage})` : "none",
              backgroundSize: "cover",
              backgroundPosition: "center center",
              aspectRatio: "3/4",
            }}
          />
          <Box sx={{ p: 2 }}>
            <Typography variant={isMobile ? "h3" : "h4"} component="h1" sx={{ mb: 1 }}>
              {general.name}
            </Typography>

            {/* Non-permanent event: start/end date + time */}
            {!isPermanent && startDate && endDate && (
              <>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon>
                    <DateIcon color={theme.palette.text.primary} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ variant: "subtitle1" }}
                    primary={formatEventDate(startDate, endDate)}
                  />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon>
                    <ClockIcon color={theme.palette.text.primary} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ variant: "subtitle1" }}
                    primary={`${fTime(startDate)} - ${fTime(endDate)}`}
                  />
                </ListItem>
              </>
            )}

            {/* Address (both) */}
            {hasAddress && (
              <ListItem
                sx={{ pl: 0, ...(googleMapsLink && { ":hover": { cursor: "pointer" } }) }}
                onClick={() => {
                  if (googleMapsLink) window.open(googleMapsLink);
                }}
              >
                <ListItemIcon>
                  <LocationIcon color={theme.palette.text.primary} />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ variant: "subtitle1" }}
                  primary={addressText}
                />
              </ListItem>
            )}

            {/* Permanent venue: open/closed now + hours */}
            {isPermanent && openingHours.length > 0 && <OpeningHours hours={openingHours} top={60} />}

            {isPermanent && program && !openingHours.length && (
              <ListItem sx={{ pl: 0 }}>
                <ListItemIcon>
                  <ClockIcon color={theme.palette.text.primary} />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ variant: "subtitle1" }}
                  primary={program}
                />
              </ListItem>
            )}

            {/* Permanent venue: rating + price range */}
            {isPermanent && (rating || priceRangeStart) ? (
              <Stack sx={{ mt: 1 }} direction="row" spacing={1} alignItems="center">
                {rating ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="subtitle1">{rating}</Typography>
                    <Iconify icon="material-symbols:star" width={20} />
                    <Typography variant="subtitle1">{`(${reviewsCount || 0})`}</Typography>
                  </Stack>
                ) : null}
                {rating && priceRangeStart ? <Typography variant="subtitle1">{" · "}</Typography> : null}
                {priceRangeStart ? (
                  <Typography variant="subtitle1">
                    {getDollarSigns(priceRangeStart)}
                    {priceRangeEnd ? ` - ${getDollarSigns(priceRangeEnd)}` : ""}
                  </Typography>
                ) : null}
              </Stack>
            ) : null}
          </Box>
        </Card>

        {/* Right column: payment, guest, reservation, order */}
        <Stack spacing={2}>
          {/* Stage 2: payment requested */}
          {paymentRequest && (
            <SectionCard>
              <Stack direction="row" alignItems="center" spacing={1}>
                <HoldingFeeIcon />
                <Typography variant="subtitle1">{translate("bookings.holdingFee")}</Typography>
              </Stack>
              <Typography sx={{ fontSize: 44, fontWeight: 800, lineHeight: 1.15, my: 1.5 }}>
                {formatMoney(paymentRequest.total, paymentRequest.currency)}
              </Typography>
              <Typography variant="body2" component="p" sx={{ mb: 2 }}>
                {translate("bookings.holdingFeeExplanation")}
              </Typography>
              {paymentRequest.emailRequired && (
                <TextField
                  fullWidth
                  type="email"
                  label={translate("bookings.emailForReceipt")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!email && !emailValid}
                  disabled={paying}
                  sx={{ mb: 2 }}
                />
              )}
              <PrimaryButton
                fullWidth
                size="large"
                disabled={!canPay}
                onClick={() => payForBooking(paymentRequest.emailRequired ? email.trim() : undefined)}
              >
                {translate("bookings.payNow")}
              </PrimaryButton>
            </SectionCard>
          )}

          {/* Guest */}
          {(!!guestName || !!guestPhone || !!guestEmail) && (
            <SectionCard>
              {!!guestName && <Typography variant="subtitle1">{guestName}</Typography>}
              {!!guestPhone && (
                <Typography variant="body2" component="p">
                  {guestPhone}
                </Typography>
              )}
              {!!guestEmail && (
                <Typography variant="body2" component="p" >
                  {guestEmail}
                </Typography>
              )}
            </SectionCard>
          )}

          {/* Reservation details */}
          <SectionCard>
            <Typography variant="subtitle1">{translate("bookings.reservationDetails")}</Typography>
            <Box sx={{ display: "flex", gap: 1, py: 1 }}>
              <Typography variant="body2">{translate("bookings.reservationId")}:</Typography>
              <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                {bookingData.id}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Chip
                label={translate(mapReservationStatusToTranslation(bookingData.status))}
                color={mapReservationStatusToColor(bookingData.status)}
              />
            </Box>
            {!!subtitle && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                {subtitle}
              </Typography>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 2 }}>
              <DetailItem icon={<GuestsIcon />}>{bookingData.numberOfGuests}</DetailItem>
              <DetailItem icon={<LocationIcon />}>{areaNames.join(", ") || "-"}</DetailItem>
              <DetailItem icon={<DateIcon />}>{fDate(bookingData.bookedDate)}</DetailItem>
              <DetailItem icon={<ClockIcon />}>{fTime(getDateWithoutTimezoneChange(bookingData.bookedHour))}</DetailItem>
              <DetailItem icon={<LengthIcon />}>{formatMinutesToHours(bookingData.duration)}</DetailItem>
            </Box>

            {bookingData.linkedEvent && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {translate("bookings.ongoingEvent")}
                </Typography>
                <LinkedEventCard
                  linkedEvent={bookingData.linkedEvent}
                  goToLinkedEvent={() => goToLinkedEvent(bookingData.linkedEvent)}
                />
              </Box>
            )}

            <Stack spacing={1.5}>
              {canReconfirm && (
                <PrimaryButton fullWidth size="large" onClick={reconfirmBooking}>
                  {translate("bookings.confirmReservation")}
                </PrimaryButton>
              )}
              {canCancel && (
                <Button
                  fullWidth
                  color="error"
                  variant="outlined"
                  size="large"
                  disabled={paying}
                  onClick={() => setOpenAlert(true)}
                >
                  {translate("bookings.cancelReservation")}
                </Button>
              )}
            </Stack>
          </SectionCard>

          {orders.length > 0 && (
            <SectionCard>
              <Typography variant="subtitle1">{translate("bookings.orderDetails")}</Typography>

              {orders.map((order, index) => {
                const cur = currencyMap[order.currency] ?? order.currency;
                const fees = [
                  { key: "discount", value: -order.discount, name: translate("checkout.discount") },
                  { key: "fee", value: order.commission, name: translate("checkout.processingFee") },
                ].filter((f) => f.value);

                return (
                  <Box
                    key={order.id}
                    sx={{
                      mt: index === 0 ? 0 : 1.5,
                      pt: index === 0 ? 0 : 1.5,
                      borderTop: index === 0 ? "none" : "1px solid #666666",
                    }}
                  >
                    <Box sx={{ py: 1, display: "flex", gap: 1 }}>
                      <Typography variant="body2">{translate("eventTicketsTable.orderID")}:</Typography>
                      <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                        {order.id}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", px: 2, pb: 1 }}>
                      <Typography variant="body2">{translate("bookings.items")}</Typography>
                      <Typography variant="body2">{translate("checkout.price")}</Typography>
                    </Box>

                    <Box sx={{ px: 2 }}>
                      {order.items.map((item, i) => (
                        <Box key={`${order.id}-${i}`} sx={{ display: "flex", mb: 1.5, gap: 1 }}>
                          <Box sx={{ flexShrink: 0 }}>
                            {item.type === "RESERVATION" ? <HoldingFeeIcon /> : item.type === "TICKET" ? <TicketIcon /> : <MenuItemIcon />}
                          </Box>
                          <Typography variant="body2" sx={{ overflowWrap: "break-word", whiteSpace: "normal", flex: 1, minWidth: 0, mt: 0.25 }}>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" textTransform="uppercase" sx={{ flexShrink: 0, ml: 2, mt: 0.25 }}>
                            {item.price ? `${item.quantity} × ${fmtNumber(item.price)} ${cur}` : translate("eventManager.free")}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {fees.length > 0 && (
                      <Box sx={{ borderTop: "1px solid #666666", pt: 1, px: 2 }}>
                        {fees.map((f) => (
                          <Box key={f.key} sx={{ display: "flex", mb: 1.5, gap: 1 }}>
                            <Typography variant="body2" sx={{ flex: 1, minWidth: 0, mt: 0.25 }}>{f.name}</Typography>
                            <Typography variant="body2" textTransform="uppercase" sx={{ flexShrink: 0, ml: 2, mt: 0.25 }}>
                              {`${fmtNumber(f.value)} ${cur}`}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Box sx={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #666666", pt: 1, px: 2 }}>
                      <Typography variant="body1" textTransform="uppercase">{translate("bookings.total")}</Typography>
                      <Typography variant="body1" textTransform="uppercase">
                        {order.total ? `${fmtNumber(order.total)} ${cur}` : translate("eventManager.free")}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </SectionCard>
          )}
        </Stack>
      </Box>

      <Dialog open={openAlert} onClose={() => setOpenAlert(false)} aria-describedby="close-reservation-alert">
        <DialogTitle>{translate("bookings.alertTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="close-reservation-alert">{translate("bookings.alertText")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              cancelBooking();
              setOpenAlert(false);
            }}
          >
            {translate("bookings.yes")}
          </Button>
          <Button onClick={() => setOpenAlert(false)}>{translate("bookings.no")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default BookingDetailsCard;